import Backdrop from '@atoms/Backdrop/Backdrop';
import CloseButton from '@atoms/CloseButton/CloseButton';
import Portal from '@atoms/Portal/Portal';
import bodyScrollLock from '@utils/helpers/bodyScrollLock';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

export interface IDrawerContentRef {
  readonly scrollToTop: () => void;
}

interface Props {
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
  readonly isOpen: boolean;
  readonly onCloseCallback?: () => void;
  readonly showClose?: boolean;
  readonly drawerClasses?: string;
  readonly maxWidthClassName?: string;
  readonly contentSpacingClassName?: string;
  readonly contentClassName?: string;
  readonly isBottomToTopSliding?: boolean;
  readonly dataTestId?: string;
}

const Drawer = forwardRef<IDrawerContentRef, PropsWithChildren<Props>>(
  (
    {
      setIsOpen,
      isOpen,
      children,
      onCloseCallback,
      showClose = true,
      drawerClasses,
      maxWidthClassName = 'max-w-[605px]',
      contentSpacingClassName = 'pt-14',
      contentClassName,
      isBottomToTopSliding,
      dataTestId = 'Drawer',
    },
    contentRef
  ) => {
    const drawerRef = useRef<HTMLDivElement>(null);
    const drawerContentRef = useRef<HTMLDivElement>(null);
    const iframeHeightRef = useRef<string | null>(null);

    const isInEditor =
      typeof window !== 'undefined' && window.top !== window.self;

    const variants = {
      hidden: {
        ...(isBottomToTopSliding ? { y: '100vh' } : { x: '100vw' }),
        transition: { duration: 0.5 },
      },
      show: {
        ...(isBottomToTopSliding ? { y: 0 } : { x: 0 }),
        transition: { duration: 0.5 },
      },
    };

    const handleClose = () => {
      setIsOpen(false);
      onCloseCallback?.();
    };

    useImperativeHandle(contentRef, () => ({
      scrollToTop: () => {
        drawerContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      },
    }));

    useEffect(() => {
      if (isOpen) {
        bodyScrollLock(true);
      }

      return () => {
        bodyScrollLock(false);
      };
    }, [isOpen]);

    useEffect(() => {
      const close = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      };

      window.addEventListener('keydown', close);
      return () => window.removeEventListener('keydown', close);
    }, [setIsOpen]);

    useEffect(() => {
      if (!isInEditor) return;

      const editorIFrame = drawerRef.current?.ownerDocument.defaultView
        ?.frameElement as HTMLIFrameElement;

      if (!editorIFrame) return;

      if (!iframeHeightRef.current) {
        iframeHeightRef.current = editorIFrame.style.height;
      }

      editorIFrame.style.height = isOpen ? '100vh' : iframeHeightRef.current;
    }, [isInEditor, isOpen]);

    return (
      <AnimatePresence>
        {isOpen && (
          <Portal key="portal" mountId="drawer-root">
            <FocusTrap
              focusTrapOptions={{
                preventScroll: true,
              }}
            >
              <div ref={drawerRef} data-testid={dataTestId}>
                <Backdrop
                  className="relative z-max"
                  key="backdrop"
                  onCloseCallback={handleClose}
                />

                <motion.div
                  key="sidebar"
                  onClick={(e) => e.stopPropagation()}
                  className={classNames(
                    'drawer fixed inset-0 left-auto z-max flex w-full flex-col overflow-hidden bg-grey-100',
                    maxWidthClassName,
                    drawerClasses
                  )}
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={variants}
                  data-testid="drawer"
                >
                  <div
                    ref={drawerContentRef}
                    className={classNames(
                      'relative flex h-full flex-col',
                      contentSpacingClassName,
                      contentClassName
                    )}
                  >
                    {/* A hidden button that steals the inital focus of the modal preventing the close icon from animating on modal open  */}
                    <button className="hidden" aria-hidden="true" />
                    {children}
                    {showClose && (
                      <CloseButton
                        onClick={handleClose}
                        className="absolute top-4 right-4 cursor-pointer text-sm"
                      />
                    )}
                  </div>
                </motion.div>
              </div>
            </FocusTrap>
          </Portal>
        )}
      </AnimatePresence>
    );
  }
);

export default Drawer;
